import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, KeyRound, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Label = ({ children }) => <label className="text-xs font-medium text-stone-500 mb-1 block">{children}</label>;

const OwnerAccessCard = ({ adminToken, venueSlug }) => {
  const [owners, setOwners] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/venue-owners`, { params: { admin_token: adminToken, venue_slug: venueSlug } });
      setOwners(res.data.owners || []);
    } catch {
      toast.error('Failed to load owner accounts');
    }
  }, [adminToken, venueSlug]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.email || !form.password) {
      toast.error('Email and password are required');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/admin/venue-owners`, { ...form, venue_slug: venueSlug }, { params: { admin_token: adminToken } });
      toast.success(res.data.message);
      setForm({ name: '', email: '', password: '' });
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save owner');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Remove owner access for ${o.email}?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/venue-owners/${o.id}`, { params: { admin_token: adminToken } });
      toast.success('Owner access removed');
      load();
    } catch {
      toast.error('Failed to remove owner');
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-5" data-testid="owner-access-card">
      <h4 className="font-medium text-stone-900 flex items-center gap-2"><UserPlus className="w-4 h-4" /> Owner Portal Access</h4>
      <p className="text-xs text-stone-500 mt-1">The property owner logs in at <span className="font-mono bg-stone-100 px-1 rounded">/owner</span> to manage availability, view leads and see booking confirmations — for this venue only.</p>

      {owners.length > 0 && (
        <div className="mt-4 space-y-2">
          {owners.map(o => (
            <div key={o.id} className="flex items-center justify-between gap-3 border border-stone-100 rounded-lg px-3 py-2.5" data-testid={`owner-row-${o.email}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-stone-900 truncate">{o.name || 'Owner'}</p>
                <p className="text-xs text-stone-500 truncate">{o.email}</p>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => remove(o)} data-testid={`owner-delete-${o.email}`}>
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><Label>Owner name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="owner-name-input" /></div>
        <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="owner-create-email-input" /></div>
        <div><Label>Password (min 8 chars)</Label><Input value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} data-testid="owner-create-password-input" /></div>
      </div>
      <Button onClick={save} disabled={busy} size="sm" className="mt-3 bg-stone-900 hover:bg-stone-800" data-testid="owner-create-btn">
        <KeyRound className="w-4 h-4 mr-1" /> {busy ? 'Saving…' : 'Create / Reset Owner Login'}
      </Button>
      <p className="text-[11px] text-stone-400 mt-2">Using an existing owner email resets their password.</p>
    </div>
  );
};

const emptyBooking = { title: '', event_date: '', end_date: '', customer_name: '', status: 'confirmed', amount_label: '', notes: '' };

const BookingsCard = ({ adminToken, venueSlug }) => {
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState(emptyBooking);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/bookings`, { params: { admin_token: adminToken, venue_slug: venueSlug } });
      setBookings(res.data.bookings || []);
    } catch {
      toast.error('Failed to load bookings');
    }
  }, [adminToken, venueSlug]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title || !form.event_date) {
      toast.error('Title and event date are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/bookings`, { ...form, venue_slug: venueSlug }, { params: { admin_token: adminToken } });
      toast.success('Booking added — visible to the owner immediately');
      setForm(emptyBooking);
      setShowForm(false);
      load();
    } catch {
      toast.error('Failed to add booking');
    }
  };

  const setStatus = async (b, status) => {
    try {
      await axios.put(`${API_URL}/admin/bookings/${b.id}`, { status }, { params: { admin_token: adminToken } });
      load();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete booking "${b.title}"?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/bookings/${b.id}`, { params: { admin_token: adminToken } });
      toast.success('Booking deleted');
      load();
    } catch {
      toast.error('Failed to delete booking');
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-5" data-testid="bookings-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-medium text-stone-900">Booking Confirmations</h4>
          <p className="text-xs text-stone-500 mt-1">Bookings your team confirms here appear in the owner's portal.</p>
        </div>
        <Button size="sm" variant={showForm ? 'outline' : 'default'} className={showForm ? '' : 'bg-stone-900 hover:bg-stone-800'} onClick={() => setShowForm(!showForm)} data-testid="booking-toggle-form-btn">
          <Plus className="w-4 h-4 mr-1" /> {showForm ? 'Cancel' : 'Add Booking'}
        </Button>
      </div>

      {showForm && (
        <div className="mt-4 border border-stone-200 rounded-lg p-4 space-y-3 bg-stone-50/60">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Title (e.g. Anita & Rohan Wedding)</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="booking-title-input" /></div>
            <div><Label>Customer name</Label><Input value={form.customer_name} onChange={e => setForm(p => ({ ...p, customer_name: e.target.value }))} data-testid="booking-customer-input" /></div>
            <div><Label>Event date</Label><Input type="date" value={form.event_date} onChange={e => setForm(p => ({ ...p, event_date: e.target.value }))} data-testid="booking-date-input" /></div>
            <div><Label>End date (optional)</Label><Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} /></div>
            <div>
              <Label>Status</Label>
              <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="w-full h-10 rounded-md border border-stone-200 bg-white px-3 text-sm" data-testid="booking-status-select">
                <option value="confirmed">Confirmed</option>
                <option value="tentative">Tentative</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div><Label>Amount label (e.g. ₹8,50,000)</Label><Input value={form.amount_label} onChange={e => setForm(p => ({ ...p, amount_label: e.target.value }))} /></div>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          <Button size="sm" onClick={create} className="bg-stone-900 hover:bg-stone-800" data-testid="booking-save-btn">Save Booking</Button>
        </div>
      )}

      {bookings.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-8">No bookings yet.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {bookings.map((b, i) => (
            <div key={b.id} className="flex items-start justify-between gap-3 border border-stone-100 rounded-lg px-4 py-3 flex-wrap" data-testid={`booking-row-${i}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-stone-900">{b.title}</p>
                <p className="text-xs text-stone-500 mt-0.5">{b.event_date}{b.end_date ? ` → ${b.end_date}` : ''}{b.customer_name ? ` · ${b.customer_name}` : ''}{b.amount_label ? ` · ${b.amount_label}` : ''}</p>
                {b.notes && <p className="text-xs text-stone-400 mt-0.5">{b.notes}</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <select value={b.status} onChange={e => setStatus(b, e.target.value)} className="h-8 rounded-md border border-stone-200 bg-white px-2 text-xs" data-testid={`booking-status-${i}`}>
                  <option value="confirmed">Confirmed</option>
                  <option value="tentative">Tentative</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(b)} data-testid={`booking-delete-${i}`}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const OwnerBookingsTab = ({ adminToken, venueSlug }) => (
  <div className="space-y-4">
    <OwnerAccessCard adminToken={adminToken} venueSlug={venueSlug} />
    <BookingsCard adminToken={adminToken} venueSlug={venueSlug} />
  </div>
);
